/**
 * @file Dashboard.js
 * @version 3.55.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-09-21
 * @modified 2026-11-03
 *
 * 3.55.0 (2026-11-03, Cam — "Add a report to reports menu that offers a
 * summary of what was invoiced in a given month and allows the user to review
 * whether contracts need making or changing for anything sold"): Reports →
 * Contracts Review ('report-contracts-review', nav.report-contracts-review).
 *
 * 3.54.0 (2026-10-30, Cam — "I don't think they need 'My Prices' … 'Reps'
 * contracts' … I don't think standard/ admins need it (duplicates Inventory >
 * Contracts)"): My Pricing and My Contracts are in the Sales menu for a REP
 * badge only (`repOnly`) — admins hold every permission and Standard User holds
 * nav.rep-contracts, so both showed for the office. Title Case labels.
 *
 * 3.53.0 (2026-10-28, Cam — rep mobile review and "make sure the 'zoom' …
 * when the app is opened on iPhone, is corrected/ defaulted/ normalised to fit
 * everything in without horizontal overflow"): Transport Planning is a
 * "use a laptop or tablet" screen on a rep's phone, like the Blend Calculator
 * (a link to it opened the whole board squeezed). And the viewport: the rep
 * shell keeps the phone's own width; anyone else on a phone-sized touch screen
 * gets the desktop laid out at its design width and scaled to fit, with no
 * sideways scroll (utils/viewportFit). Pinch-zoom still works.
 * 3.52.0 (2026-10-28, Cam — P7): "My pricing" (RepPricing, menu rep-pricing,
 * nav.rep-pricing) under Sales — the reps' own pricing screen; office staff
 * holding it choose a rep and see that rep's screen, read only.
 * 3.51.0 (2026-10-28, Cam — O4 / proposal D12): "My contracts" (a rep) /
 * "Reps' contracts" (the office) under Sales — RepContracts, nav.rep-contracts.
 *
 * 3.49.0 (2026-10-21, Cam): the side nav can have TWO sections open at once
 * (a third closes the one opened longest ago), and every section closes when
 * a screen is navigated to.
 *
 * 3.48.0 (2026-10-21, Cam): the Pricing tab (Sales Orders) and the Costing tab
 * (Purchase Orders) are gone — Pricing & Costing replaced them. PriceOrders.js
 * and PricePOs.js stay in the code base, unreferenced; the old price-orders /
 * price-pos links open Pricing & Costing.
 *
 * 3.47.0 (2026-10-19, Cam): a page's tab survives a browser refresh — the tab
 * chosen on each page is kept in sessionStorage (this browser tab only).
 *
 * 3.46.0 (2026-10-19, Cam): Edit from a view window (utils/editHandoff): when
 * the editor it hands to is already the open screen, EDIT_EVENT mounts it
 * afresh so it reads the hand-off. Pricing & Costing gets onNavigate (Edit
 * invoice).
 *
 * 3.45.0 (2026-10-17, Cam and Jade's Pricing & Costing Review): a "Pricing &
 * Costing" tab on Sales Orders and on Purchase Orders, opening the same
 * screen (PricingCosting, D10) — beside the old Pricing and Costing tabs while
 * Jade checks the proposals against her own (the review's step 1); those go
 * once she is happy.
 *
 * 3.44.0 (2026-10-15, Cam): "Alerts" in the status bar for standard users and
 * admins (StatusBarAlerts — bold, var(--color-error), like Issues Logged,
 * which admins keep as well), fed by the 20-second notices summary poll
 * (useNoticeAlertsPoll); the two share the .status-alert styles.
 *
 * 3.43.0 (2026-10-15, Cam): the status bar's Xero lock is green and closed
 * when Xero is locked to the end of last month (or later), red and open when
 * behind or not set (utils/xeroLockMonth); the tooltip says which.
 *
 * 3.42.0 (2026-10-15, Cam): Reports → Xero Reports ('report-xero', a tab per
 * report: Accrual, Despatch, AHDB Levy, Stock Pricing, Customer Tonnage,
 * Price Check), under nav.report-xero.
 *
 * 3.41.0 (2026-10-13, Jade): the 'create-bill' screen names itself — Add /
 * Edit Supplier Credit or Add / Edit Bill (CreateBill's onTitleChange).
 *
 * 3.40.0 (2026-10-12, Cam): Bulk Add Bills — a screen of its own
 * ('bulk-add-bills', reached from Search Bills' header and a second shortcut
 * left of the + beside Bills in the Purchases menu), under the Bills
 * permission like Add Bill.
 */

import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { API_URL } from './config';
import './Dashboard.css';
import EntityModalProvider from './components/EntityModalProvider';
import { SearchRevealContext } from './hooks/useOpenInSearch';
import SearchContacts from './components/SearchContacts';
import ContactGroups from './components/ContactGroups';
import SearchInvoices from './components/SearchInvoices';
import CreditAllocations from './components/CreditAllocations';
import DashboardHome from './components/DashboardHome';
import SearchPurchaseOrders from './components/SearchPurchaseOrders';
import CreditLimitManagement from './components/CreditLimitManagement';
import SearchPayments from './components/SearchPayments';
import { useNavStore } from './store/navStore';
import { guidedNow } from './utils/guidedMode';
import SearchInventory from './components/SearchInventory';
import SpotPricing from './components/SpotPricing';
import CompoundPricing from './components/CompoundPricing';
import BlendPricing from './components/BlendPricing';
import StraightsPricing from './components/StraightsPricing';
import GenericContractPricing from './components/GenericContractPricing';
import MineralPricing from './components/MineralPricing';
import MilkPowderPricing from './components/MilkPowderPricing';
import Additives from './components/Additives';
import Formulations from './components/Formulations';
import BlendCalculator from './components/BlendCalculator';
import BlendMaterials from './components/BlendMaterials';
import BlendSavedList from './components/BlendSavedList';
import BlendSpecs from './components/BlendSpecs';
import InventoryPriceChanges from './components/InventoryPriceChanges';
import ProductGroups from './components/ProductGroups';
import InvoiceDetailReport from './components/InvoiceDetailReport';
import MapOfAgReport from './components/MapOfAgReport';
import AgedDebtReport from './components/AgedDebtReport';
import XeroReports from './components/XeroReports';
import ContractsReview from './components/ContractsReview';
import UsersDashboard from './components/UsersDashboard';
import ActivityDashboard from './components/ActivityDashboard';
import CreateCustomerOrder from './components/CreateCustomerOrder';
import SearchCustomerOrders from './components/SearchCustomerOrders';
import { hasPermission, isReadOnly, xeroRequirement, badgeOf } from './utils/permissions';
import { AccessProvider } from './hooks/useAccess';
import useXeroLock, { publishXeroLock } from './hooks/useXeroLock';
import { lockMonthState } from './utils/xeroLockMonth';
import { formatDate } from './utils/dateFormatters';
import AccessDenied from './components/AccessDenied';
import XeroRequiredGate from './components/XeroRequiredGate';
import { SESSION_EXPIRED_EVENT } from './utils/readOnlyFetch';
import StandingOrders from './components/StandingOrders';
import RepContracts from './components/RepContracts';
import RepMobileNav from './components/RepMobileNav';
import RepMobileHeader, { greetingFor } from './components/RepMobileHeader';
import useViewport from './hooks/useViewport';
import { applyViewport, viewportContentFor, isSmallTouchScreen, DEVICE_VIEWPORT } from './utils/viewportFit';
import './RepMobile.css';
import Prescriptions from './components/Prescriptions';
import Complaints from './components/Complaints';
import CreatePurchaseOrder from './components/CreatePurchaseOrder';
import PricingCosting from './components/PricingCosting';
import RepPricing from './components/RepPricing';
import { EDIT_EVENT } from './utils/editHandoff';
import SearchBills from './components/SearchBills';
import SearchRepeatingBills from './components/SearchRepeatingBills';
import CreateBill from './components/CreateBill';
import BulkAddBills from './components/BulkAddBills';
import CreateInvoice from './components/CreateInvoice';
import AlertDialog from './components/AlertDialog';
import LoadingSpinner from './components/LoadingSpinner';
import ConfirmDialog from './components/ConfirmDialog';
import GenerateStatements from './components/GenerateStatements';
import StatementRunHistory from './components/StatementRunHistory';
import FleetTracking from './components/FleetTracking';
import TransportPlanning from './components/TransportPlanning';
import HauliersManager from './components/HauliersManager';
import MillEmailsManager from './components/MillEmailsManager';
import UnlockPasswordSettings from './components/UnlockPasswordSettings';
import DayPlanSettings from './components/DayPlanSettings';
import DriversManager from './components/DriversManager';
import VehiclesManager from './components/VehiclesManager';
import SearchHaulageJobs from './components/SearchHaulageJobs';
import CreateHaulageJob from './components/CreateHaulageJob';
import HaulageJobsPlanning from './components/HaulageJobsPlanning';
import RepsManager from './components/RepsManager';
import TillSales from './components/TillSales';
import SyncProducts from './components/SyncProducts';
import SyncContacts from './components/SyncContacts';
import ContactEditModal from './components/ContactEditModal';
import ContextMenu from './components/ContextMenu';
import { flattenMenuItems } from './utils/flattenMenuItems';
import UserProfile from './components/UserProfile';
import useUserPreferences from './hooks/useUserPreferences';
import useDashboardSettings from './hooks/useDashboardSettings';
import ReactDOM from 'react-dom';
import CustomIcon from './components/CustomIcon';
import MaintenanceBanner from './components/MaintenanceBanner';
import XeroScopeBanner from './components/XeroScopeBanner';
import XeroHealthBanner from './components/XeroHealthBanner';
import useSystemStatus from './hooks/useSystemStatus';
import { useNoticeAlertsPoll } from './hooks/useNoticeAlerts';
import StatusBarAlerts, { alertsFor } from './components/StatusBarAlerts';
import useKeyboardInset from './hooks/useKeyboardInset';
import Modal from './components/Modal';
import SystemLogAlerts from './components/SystemLogAlerts';
import DataQueryModal from './components/DataQueryModal';

/**
 * Below this width the nav starts collapsed and overlays the content.
 *
 * 1600 rather than a phone breakpoint: this is a desktop application and the
 * question is not "does it fit" but "is 260px of permanent chrome worth it".
 * On a 1440-wide laptop it is most of the room a wide table needs; on a large
 * monitor it costs nothing.
 */
const NAV_OVERLAY_WIDTH = 1600;

/**
 * SVG tree line for nav sub-items.
 * Measures sub-item positions within the container and draws a trunk + curved
 * branches. Sub-items are grouped by any `.nav-sub-divider` nodes that appear
 * between them — one trunk is drawn per group so dividers create visible
 * breaks in the tree.
 */
function NavTree({ containerRef, isCollapsed }) {
  const [paths, setPaths] = useState('');
  const svgRef = useRef(null);

  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    // Walk children in DOM order. Each contiguous run of .nav-item.sub-item
    // forms a group; a .nav-sub-divider or a .nav-favourite-submenu (folder)
    // ends the current group and starts a new one so the trunk breaks cleanly
    // around those nodes.
    const groups = [];
    let current = [];
    for (const child of container.children) {
      if (child.classList.contains('nav-sub-divider') ||
          child.classList.contains('nav-favourite-submenu')) {
        if (current.length > 0) { groups.push(current); current = []; }
      } else if (child.classList.contains('nav-item') && child.classList.contains('sub-item')) {
        current.push(child);
      }
    }
    if (current.length > 0) groups.push(current);
    if (groups.length === 0) { setPaths(''); return; }

    const containerRect = container.getBoundingClientRect();
    const trunkX = 0;
    const branchEndX = isCollapsed ? 12 : 14;
    const branchOffset = 8;
    const trunkTopPad = 10;

    let d = '';

    for (const group of groups) {
      const points = group.map(item => {
        const rect = item.getBoundingClientRect();
        return rect.top - containerRect.top + rect.height / 2;
      });
      if (points.length === 0) continue;

      const trunkStart = points[0] - branchOffset - trunkTopPad;
      const trunkEnd = points[points.length - 1] - branchOffset;
      d += `M ${trunkX} ${trunkStart} L ${trunkX} ${trunkEnd} `;

      points.forEach(y => {
        const branchStartY = y - branchOffset;
        const branchEndY = y;
        d += `M ${trunkX} ${branchStartY} `;
        d += `C ${trunkX} ${branchStartY + branchOffset}, ${branchEndX} ${branchEndY - branchOffset * 0.3}, ${branchEndX} ${branchEndY} `;
      });
    }

    setPaths(d);
  }, [containerRef, isCollapsed]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  }, [containerRef, measure]);

  if (!paths) return null;

  return (
    <svg className="nav-tree-svg" ref={svgRef}>
      <path className="nav-tree-path" d={paths} />
    </svg>
  );
}

/*
 * Screens a rep can reach on a laptop but not on a phone, and why.
 *
 * RepMobileNav declines to offer these, but the hashes are typeable and
 * bookmarkable — and a persisted activeMenu can land on one after a rep
 * switches device — so the route says so too. A sentence rather than a blank
 * screen: a rep looking for the Blend Calculator should learn where it is, not
 * conclude it has been taken away.
 */
const REP_DESKTOP_ONLY = {
  'blend-calculator':
    'The Blend Calculator needs a bigger screen than a phone can give it. Everything else is here.',
  // Cam, 28 Oct 2026 (rep mobile review): a link to it opened the whole
  // planning board squeezed onto the phone.
  'transport-planning':
    'Transport Planning is a board of every lorry and load for the day — it needs a laptop or tablet. Today\'s Deliveries on Home shows yours.',
  formulations:
    'Formulations are a dense table of analysis figures. They read properly on a laptop, and badly on a phone.'
};

// Pages that render as tabbed content: a .content-tab-strip above the page
// panel (rendered by Dashboard) plus the component for the active tab. Add an
// entry here to make any page tabbed.
const TABBED_PAGES = {
  'search-contacts': [
    { key: 'contacts', label: 'Contacts', icon: 'group' },
    { key: 'groups', label: 'Contact Groups', icon: 'groups', perm: 'nav.search-contacts.groups' },
    { key: 'reps', label: 'Reps', icon: 'badge', perm: 'nav.search-contacts.reps' }
  ],
  'credit-limits': [
    { key: 'credit', label: 'Credit Control', icon: 'account_balance' },
    { key: 'credits', label: 'Unallocated Credits', icon: 'call_split', perm: 'nav.credit-limits.credits' },
    { key: 'payments', label: 'Payments', icon: 'payments', perm: 'nav.credit-limits.payments' }
  ],
  'search-customer-orders': [
    { key: 'orders', label: 'Sales Orders', icon: 'sell' },
    { key: 'pricing-costing', label: 'Pricing & Costing', icon: 'price_check', perm: 'nav.search-customer-orders.pricing' }
  ],
  'search-till-sales': [
    { key: 'transactions', label: 'Transactions', icon: 'storefront' },
    { key: 'contacts', label: 'Contacts', icon: 'how_to_reg', perm: 'nav.search-till-sales.contacts' },
    { key: 'inventory', label: 'Inventory', icon: 'cloud_sync', perm: 'nav.search-till-sales.inventory' }
  ],
  'search-purchase-orders': [
    { key: 'orders', label: 'Purchase Orders', icon: 'shopping_cart' },
    { key: 'pricing-costing', label: 'Pricing & Costing', icon: 'price_check', perm: 'nav.search-purchase-orders.costing' }
  ],
  'search-inventory': [
    { key: 'inventory', label: 'Inventory', icon: 'inventory_2' },
    { key: 'additives', label: 'Additives', icon: 'science', perm: 'nav.search-inventory.additives' },
    { key: 'minerals', label: 'Minerals', icon: 'diamond', perm: 'nav.search-inventory.minerals' },
    { key: 'pricing', label: 'Pricing', icon: 'planner_review', perm: 'nav.search-inventory.pricing' },
    // Product groups (O1, Cam 28 Oct 2026): anyone on Manage Inventory reads; changes need inventory.edit
    { key: 'product-groups', label: 'Product Groups', icon: 'stacks' }
  ],
  'milk-powder-pricing': [
    { key: 'costs', label: 'Costing', icon: 'payments' },
    { key: 'contracts', label: 'Pricing', icon: 'handshake' }
  ],
  // Minerals here is per-customer mineral CONTRACT pricing (category 4) — the
  // product-level mineral price list lives on Manage Inventory → Minerals.
  'contract-pricing': [
    { key: 'compounds', label: 'Compounds', icon: 'nest_thermostat_gen_3' },
    { key: 'blends', label: 'Blends', icon: 'target', perm: 'nav.contract-pricing.blends' },
    { key: 'straights', label: 'Straights', icon: 'radio_button_unchecked', perm: 'nav.contract-pricing.straights' },
    { key: 'spot', label: 'Spot', icon: 'identity_platform', perm: 'nav.contract-pricing.spot' },
    { key: 'minerals', label: 'Minerals', icon: 'diamond', perm: 'nav.contract-pricing.minerals' },
    { key: 'bags', label: 'Bags', icon: 'shopping_bag', perm: 'nav.contract-pricing.bags' },
    { key: 'other', label: 'Other', icon: 'category', perm: 'nav.contract-pricing.other' }
  ],
  'report-map-of-ag': [
    { key: 'contacts', label: 'Map of Ag', icon: 'groups', perm: 'nav.report-map-of-ag.contacts' },
    { key: 'history', label: 'History', icon: 'history', perm: 'nav.report-map-of-ag.history' },
    { key: 'processors', label: 'Processors', icon: 'factory', perm: 'nav.report-map-of-ag.processors' },
    { key: 'product-mapping', label: 'Mapping', icon: 'swap_horiz', perm: 'nav.report-map-of-ag.product-mapping' }
  ],
  // The reports once exported from Xero; the tab key is the report's key on
  // /api/xero-reports.
  'report-xero': [
    { key: 'accrual', label: 'Accrual', icon: 'pending_actions' },
    { key: 'despatch', label: 'Despatch', icon: 'local_shipping' },
    { key: 'levy', label: 'AHDB Levy', icon: 'grass' },
    { key: 'stock-pricing', label: 'Stock Pricing', icon: 'inventory_2' },
    { key: 'customer-tonnage', label: 'Customer Tonnage', icon: 'scale' },
    { key: 'price-check', label: 'Price Check', icon: 'price_check' }
  ],
  'transport-settings': [
    { key: 'hauliers', label: 'Hauliers', icon: 'id_card' },
    { key: 'drivers', label: 'Drivers', icon: 'badge' },
    { key: 'vehicles', label: 'Vehicles', icon: 'local_shipping' },
    // Where each mill's order summary is sent from transport planning.
    { key: 'mill-emails', label: 'Mill Emails', icon: 'forward_to_inbox' },
    // The shared password that unlocks a locked day. Seen by admins and those
    // granted it by name; changed by admins only.
    { key: 'unlock-password', label: 'Unlock Password', icon: 'key', perm: 'transport.unlock_password.view' },
    // Lorry day planning on the bulk board: seen by all, switched by admins.
    // (The key is the old Loads and Drops one, so saved tabs still land here.)
    { key: 'loads-drops', label: 'Lorry Day Planning', icon: 'route' }
  ],
  'blend-calculator': [
    { key: 'calculator', label: 'Calculator', icon: 'calculate' },
    // Compare is no longer a tab. It is a mode inside the Calculator, because
    // comparing means loading one blend beside another and editing one of them
    // live — which needs the grid, the materials and the analysis panel that
    // only the Calculator has.
    { key: 'materials', label: 'Materials', icon: 'inventory_2', perm: 'nav.blend-calculator.materials' },
    { key: 'specs', label: 'Specs', icon: 'rule', perm: 'nav.blend-calculator.specs' },
    { key: 'blends', label: 'Saved Blends', icon: 'bookmarks', perm: 'nav.blend-calculator.blends' },
    // Blends and formulations are worked on together — a blend is often started
    // from a formulation — so each screen carries a jump to the other rather
    // than sending people back through the menu.
    { key: 'goto:formulations', label: 'Formulations', icon: 'science', jumpTo: 'formulations', perm: 'nav.formulations' }
  ],
  users: [
    { key: 'users', label: 'Users', icon: 'group' },
    { key: 'roles', label: 'Roles', icon: 'shield_person' }
  ],
  formulations: [
    { key: 'formulations', label: 'Formulations', icon: 'science' },
    { key: 'goto:blend-calculator', label: 'Blend Calculator', icon: 'calculate', jumpTo: 'blend-calculator', perm: 'blend_calculator' }
  ]
};

// Screens that used to have their own submenu entry and are now tabs. Saved
// favourites and context-menu preferences store menu ids, and #hash links are
// bookmarked, so both keep resolving: navigating to the old id lands on the
// page that absorbed it with the right tab already selected.
const LEGACY_TAB_ROUTES = {
  'contact-groups': { menu: 'search-contacts', tab: 'groups' },
  'reps': { menu: 'search-contacts', tab: 'reps' },
  // Price Orders and Price POs were retired for Pricing & Costing (Cam, 21 Oct
  // 2026); their old links open it.
  'price-orders': { menu: 'search-customer-orders', tab: 'pricing-costing' },
  'sync-contacts': { menu: 'search-till-sales', tab: 'contacts' },
  'sync-products': { menu: 'search-till-sales', tab: 'inventory' },
  'price-pos': { menu: 'search-purchase-orders', tab: 'pricing-costing' },
  'pricing-costing': { menu: 'search-customer-orders', tab: 'pricing-costing' },
  // Now a header button rather than a page — land on the screen that hosts it.
  'email-not-sent-report': { menu: 'search-purchase-orders', tab: 'orders' },
  'additives': { menu: 'search-inventory', tab: 'additives' },
  'price-changes': { menu: 'search-inventory', tab: 'pricing' },
  'product-groups': { menu: 'search-inventory', tab: 'product-groups' },
  'spot-pricing': { menu: 'contract-pricing', tab: 'spot' },
  // Where a modal's "Find in …" lands when the list is a tab (useOpenInSearch).
  'search-payments': { menu: 'credit-limits', tab: 'payments' },
  'transport-drivers': { menu: 'transport-settings', tab: 'drivers' },
  'transport-vehicles': { menu: 'transport-settings', tab: 'vehicles' }
};

// sessionStorage key for each tabbed page's chosen tab (survives a refresh).
const PAGE_TABS_KEY = 'dashboard.pageTabs';

function Dashboard({ user, onLogout, onReady }) {
  // Initial activeMenu reads from window.location.hash so refresh restores
  // the previous screen and #deep-links work. Validation against menuItems
  // happens in a useEffect once the menu config is built.
  const initialHash = typeof window !== 'undefined' ? window.location.hash.slice(1) : '';
  // A bookmarked hash for a page that is now a tab resolves before the first
  // paint, so the screen never flashes empty on the way to being corrected.
  const initialLegacy = LEGACY_TAB_ROUTES[initialHash];
  const [activeMenu, setActiveMenu] = useState(initialLegacy?.menu || initialHash || 'home');
  const [activeMainMenu, setActiveMainMenu] = useState('home');
  // Active tab per tabbed page (keyed by activeMenu). The content-tab-strip
  // sits above the page panel; Dashboard owns it so it can be a sibling of
  // .content-inner. Persists across navigation as Dashboard stays mounted.
  //
  // Kept in sessionStorage too (Cam, 19 Oct 2026: "When user refreshes
  // browser they should stay on the tab they are on"): a refresh mounts
  // Dashboard afresh. A stored tab this user may not see falls back to the
  // first visible one (activeTabFor), as any stale tab does.
  const [pageTabs, setPageTabs] = useState(() => {
    let saved = {};
    try {
      const raw = JSON.parse(window.sessionStorage.getItem(PAGE_TABS_KEY) || '{}');
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) saved = raw;
    } catch (e) { /* unreadable or blocked storage — start from nothing */ }
    return initialLegacy ? { ...saved, [initialLegacy.menu]: initialLegacy.tab } : saved;
  });
  useEffect(() => {
    try { window.sessionStorage.setItem(PAGE_TABS_KEY, JSON.stringify(pageTabs)); } catch (e) { /* storage blocked */ }
  }, [pageTabs]);
  /**
   * The tabs on a page, narrowed to the ones this user may see. A tab is its own
   * permission (nav.<menu-id>.<tab-key>) because a page and one of its tabs are
   * genuinely separable — a rep sees Sales Orders but not its Pricing tab.
   */
  const visibleTabsFor = (page) =>
    (TABBED_PAGES[page] || []).filter(t => !t.perm || hasPermission(user, t.perm));

  /**
   * The active tab, falling back to the first VISIBLE one. The fallback matters:
   * a stored tab (from a favourite, a #hash or a permission revoked while the
   * tab was open) can name a tab this user no longer has, and landing on a blank
   * panel would read as the page being broken.
   */
  const activeTabFor = (page) => {
    const tabs = visibleTabsFor(page);
    if (!tabs.length) return null;
    const stored = pageTabs[page];
    return stored && tabs.some(t => t.key === stored) ? stored : tabs[0].key;
  };
  const setPageTab = (page, key) => setPageTabs(prev => ({ ...prev, [page]: key }));
  // Bumped when an Edit hands a document to the editor that is already open
  // (utils/editHandoff): the editor is keyed on it, so it mounts afresh and
  // reads the hand-off.
  const [editRemount, setEditRemount] = useState(0);
  const activeMenuRef = useRef(null);
  activeMenuRef.current = activeMenu;
  useEffect(() => {
    const onEdit = (e) => { if (e.detail?.menu && e.detail.menu === activeMenuRef.current) setEditRemount(n => n + 1); };
    window.addEventListener(EDIT_EVENT, onEdit);
    return () => window.removeEventListener(EDIT_EVENT, onEdit);
  }, []);
  // A blend handed from the Saved Blends tab to the Calculator. Held here
  // because the two tabs never exist at the same time.
  const [blendToLoad, setBlendToLoad] = useState(null);
  // The side nav's open sections (Cam, 21 Oct 2026): up to TWO at once —
  // opening a third closes the one opened longest ago — and all of them close
  // whenever a screen is navigated to (the effect on activeMenu just below).
  const MAX_OPEN_SECTIONS = 2;
  const [expandedSections, setExpandedSections] = useState([]);
  const isSectionOpen = (id) => expandedSections.includes(id);
  const toggleSection = (id) => setExpandedSections(prev => (
    prev.includes(id)
      ? prev.filter(x => x !== id)
      : [...prev, id].slice(-MAX_OPEN_SECTIONS)
  ));
  const collapseSections = () => setExpandedSections([]);
  useEffect(() => { setExpandedSections([]); }, [activeMenu]);
  const [expandedFavSubmenus, setExpandedFavSubmenus] = useState(new Set());

  // Collapse any open Favourites submenus whenever the Favourites nav
  // section itself closes, so reopening it doesn't reveal them pre-expanded.
  useEffect(() => {
    if (!expandedSections.includes('favourites') && expandedFavSubmenus.size > 0) {
      setExpandedFavSubmenus(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedSections]);
  const [xeroConnected, setXeroConnected] = useState(false);
  const [checkingXero, setCheckingXero] = useState(true);
  // 'user': this person connects their own Xero (the gate applies). 'custom':
  // their work goes through the system's own connection — the whole system's
  // mode, or set for them on the Users screen — so there is no gate, no
  // reconnect and no scope prompt. The server decides, per person.
  const [xeroMode, setXeroMode] = useState('user');
  const xeroRecheckTimer = useRef(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  /**
   * The nav starts collapsed on a laptop and stays open on a large display.
   *
   * 260px of permanent chrome is affordable on a 27" monitor and is not on a
   * 1440-wide laptop, where it is most of the room a wide table needs. Below the
   * threshold it also OVERLAYS rather than pushing the content across, so
   * opening the menu does not reflow the page underneath it.
   *
   * Read once at mount rather than tracked: someone who collapses or expands it
   * has made a choice, and having a window resize overrule that is worse than
   * the layout being briefly wrong for a resize nobody does mid-task.
   */
  const [isCompactNav] = useState(() => window.innerWidth < NAV_OVERLAY_WIDTH);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => window.innerWidth < NAV_OVERLAY_WIDTH);
  const collapseTimer = useRef(null);

  /**
   * Close the nav once a navigation has actually happened.
   *
   * Only on a compact display, and only for a leaf — clicking a menu that opens
   * a submenu has not navigated anywhere, and closing the nav under that click
   * would make the submenu unreachable. The delay lets the item's selected state
   * register before it slides away, so the click is seen to have landed.
   */
  const collapseAfterNavigation = useCallback(() => {
    if (!isCompactNav) return;
    if (collapseTimer.current) clearTimeout(collapseTimer.current);
    collapseTimer.current = setTimeout(() => setSidebarCollapsed(true), 260);
  }, [isCompactNav]);

  useEffect(() => () => {
    if (collapseTimer.current) clearTimeout(collapseTimer.current);
  }, []);
  const [contentFading, setContentFading] = useState(false);
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');
  const contentInnerRef = useRef(null);

  /*
   * Whatever else happens, the content is visible and at the top once the
   * screen has actually changed.
   *
   * navigateToMenu fades the panel out, swaps activeMenu inside a 300ms
   * timeout, and fades back in. `.content-inner.fading` is `opacity: 0;
   * pointer-events: none`, so if that timer is ever missed, superseded or
   * throttled — which a phone browser will do — the new screen renders
   * invisible and untappable, and looks for all the world like it did not
   * load. Keying the reset off activeMenu instead means the fade can only ever
   * be a transition, never a state something can get stuck in.
   *
   * The scroll reset is the other half: .content-inner is the scroll
   * container and nothing resets it between screens, so arriving from a list
   * that was scrolled a long way down lands you past the end of the new
   * screen — a blank panel, again indistinguishable from one that did not
   * load. A browser does this for a page navigation; an in-place swap has to
   * do it itself.
   */
  useEffect(() => {
    setContentFading(false);
    const el = contentInnerRef.current;
    if (el && el.scrollTop !== 0) el.scrollTop = 0;
  }, [activeMenu]);

  /*
   * The rep phone shell: a REP badge on a narrow viewport, and nothing else.
   * An office user on a phone keeps today's behaviour untouched, and a rep at a
   * desk gets the normal shell with the fixed dashboard.
   *
   * Deliberately NOT isReadOnly(user) — that would also catch a future
   * non-rep read-only badge, which has no reason to get a rep's navigation.
   *
   * This branches the CHROME only. activeMenu, hash routing, the permission
   * gate, the Xero gate and the whole screen-render chain below are shared, so
   * the two shells cannot drift apart the first time a route is added.
   */
  const { isPhone } = useViewport();
  const repMobile = badgeOf(user) === 'REP' && isPhone;

  // Mid-day update warning / lock / post-deploy refresh. See MaintenanceBanner.
  const {
    lock: maintLock,
    phase: maintPhase,
    secondsUntilLock,
    xeroHealth,
  } = useSystemStatus();
  // The notices summary — this person's unacknowledged alerts and a live
  // signature (every 20s, like the status poll above). Standard users and
  // admins only: no notice is for a rep.
  useNoticeAlertsPoll({ enabled: alertsFor(user) });
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', type: 'info', confirmText: 'Confirm', onConfirm: null });
  const [appVersion, setAppVersion] = useState('');
  const sidebarRef = useRef(null);
  const sectionRefs = useRef({});
  const [showContactModal, setShowContactModal] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [pageTitleOverride, setPageTitleOverride] = useState(null);

  // Logger alerting — admin only. The status bar shows a badge whenever
  // something concerning in the system's logs is not yet marked actioned, so
  // a problem doesn't depend on somebody opening the logger widget to be
  // noticed. Clicking it opens the same alerting panel the widget renders.
  const [issueCount, setIssueCount] = useState(0);
  const xeroLock = useXeroLock();
  // Locked to the end of last month (or later) = green and closed; behind or
  // not set = red and open (utils/xeroLockMonth — the dashboard notice and
  // the save's question read the same rule).
  const lockState = lockMonthState(xeroLock);
  const [showLockDate, setShowLockDate] = useState(false);
  const [lockRefreshing, setLockRefreshing] = useState(false);
  // The date and refresh button fold away again 1s after the pointer leaves
  // the status bar, and when the dialog after a refresh is closed.
  const lockCollapseTimer = useRef(null);
  const lockCollapseOnDialogClose = useRef(false);
  const cancelLockCollapse = () => {
    clearTimeout(lockCollapseTimer.current);
    lockCollapseTimer.current = null;
  };
  const scheduleLockCollapse = () => {
    cancelLockCollapse();
    if (!showLockDate || lockRefreshing) return;
    lockCollapseTimer.current = setTimeout(() => setShowLockDate(false), 1000);
  };
  useEffect(() => () => clearTimeout(lockCollapseTimer.current), []);
  // Re-read Xero's lock now. The answer is stored on the server and every
  // other tab picks it up from the status poll (useSystemStatus).
  const refreshXeroLock = async () => {
    cancelLockCollapse();
    lockCollapseOnDialogClose.current = true;
    setLockRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/xero-lock/refresh`, { method: 'POST', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'The lock date could not be refreshed');
      publishXeroLock(data.xeroLock);
      const fmt = (d) => (d ? formatDate(d) : 'none');
      const was = data.before?.effectiveLockDate || null;
      const now = data.xeroLock?.effectiveLockDate || null;
      setAlertDialog({
        isOpen: true,
        type: 'success',
        message: (was === now
          ? `Xero lock date checked: unchanged at ${fmt(now)}.`
          : `Xero lock date updated: ${fmt(was)} → ${fmt(now)}.`)
          + `\nPeriod lock: ${fmt(data.xeroLock?.periodLockDate)} · Year-end lock: ${fmt(data.xeroLock?.endOfYearLockDate)}.`
          + (now ? `\nNothing dated on or before ${fmt(now)} can be saved.` : '\nEvery date is open.')
      });
    } catch (e) {
      setAlertDialog({ isOpen: true, type: 'error', message: e.message });
    } finally {
      setLockRefreshing(false);
    }
  };
  const [showIssuesModal, setShowIssuesModal] = useState(false);

  // Ask the Data. `available` is tri-state: null while unknown, so the button
  // does not appear and then vanish on a slow probe. The server reports
  // availability rather than failing, because the feature needs an API key that
  // a given environment may not have.
  const [showDataQuery, setShowDataQuery] = useState(false);
  const [dataQueryReady, setDataQueryReady] = useState(null);

  useEffect(() => {
    if (!hasPermission(user, 'data_query.ask')) { setDataQueryReady(false); return; }
    let cancelled = false;
    fetch(`${API_URL}/api/data-query/available`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { available: false }))
      .then((d) => { if (!cancelled) setDataQueryReady(Boolean(d.available)); })
      .catch(() => { if (!cancelled) setDataQueryReady(false); });
    return () => { cancelled = true; };
  }, [user]);

  // User preferences (theme color, wallpaper, context menu)
  const { preferences, loading: prefsLoading, saving: savingPrefs, savePreferences } = useUserPreferences();

  // Dashboard settings (layout, widget placement)
  const {
    settings: dashboardSettings,
    loading: settingsLoading,
    saving: savingDashboard,
    saveSettings: saveDashboardSettings,
    saveWidgetSettings,
    resetSettings: resetDashboardSettings
  } = useDashboardSettings();

  // Signal readiness to parent once preferences and settings have loaded
  const readyFired = useRef(false);
  useEffect(() => {
    if (!prefsLoading && !settingsLoading && !readyFired.current) {
      readyFired.current = true;
      // Allow one frame for the DOM to paint with loaded data
      requestAnimationFrame(() => {
        if (onReady) onReady();
      });
    }
  }, [prefsLoading, settingsLoading, onReady]);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark';
    document.documentElement.setAttribute('data-theme', savedTheme);

    // Fetch version info
    fetch('/api/version')
      .then(res => res.json())
      .then(data => setAppVersion(data.version))
      .catch(() => setAppVersion('?.?.?'));
  }, []);

  useEffect(() => {
    // Wait for session to establish before checking Xero
    const timer = setTimeout(() => {
        checkXeroConnection();
    }, 500);

    // This five-minute poll is also what keeps XeroRequiredGate honest. A
    // refresh token lapses after 60 days of disuse and can be revoked in Xero
    // at any time, both of which happen with a tab already open — so the gate
    // appears within five minutes of the connection failing, rather than on the
    // next unlucky save. checkXeroConnection returns immediately for accounts
    // that cannot connect, so this costs a rep nothing.
    const interval = setInterval(checkXeroConnection, 5 * 60 * 1000);

    /*
     * The session, checked for EVERYONE.
     *
     * checkXeroConnection returns immediately for accounts that cannot connect,
     * so it can never notice a rep's session lapsing — and a tab nobody is
     * touching makes no requests for the fetch guard to see either. This is a
     * cheap endpoint the app already calls on boot; a 401 from it means the
     * cookie is gone, and any other failure is a network blip and ignored.
     */
    const sessionCheck = setInterval(() => {
      fetch(`${API_URL}/api/users/me`, { credentials: 'include' })
        .then((r) => { if (r.status === 401) setSessionExpired(true); })
        .catch(() => { /* offline or a blip — not evidence of expiry */ });
    }, 5 * 60 * 1000);

    return () => {
        clearTimeout(timer);
        clearInterval(interval);
        clearInterval(sessionCheck);
        clearTimeout(xeroRecheckTimer.current);
    };
    // checkXeroConnection now reads `user` (to skip accounts that cannot connect),
    // which makes it reactive as far as the lint rule is concerned. This effect
    // still belongs on mount only: re-running it on a new `user` identity would
    // tear down and rebuild the poll for no gain, and the function reads the
    // current `user` from the closure it is called in either way.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll the outstanding-issue count for the status bar. Admin only — the
  // endpoint is admin-gated server-side too, so a non-admin would just get
  // 403s. Uses the cheap count endpoint rather than fetching the alert list,
  // since this polls far more often than the panel is opened.
  useEffect(() => {
    if (!user.is_admin) return undefined;

    let cancelled = false;
    const fetchIssueCount = () => {
      fetch(`${API_URL}/api/system-logs/alerts/count`, { credentials: 'include' })
        .then(res => (res.ok ? res.json() : null))
        .then(data => { if (!cancelled && data) setIssueCount(data.total || 0); })
        .catch(() => { /* transient — keep the last known count */ });
    };

    const timer = setTimeout(fetchIssueCount, 1500);
    const interval = setInterval(fetchIssueCount, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [user.is_admin]);

  // Handle OAuth callback messages
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const xeroConnected = params.get('xero_connected');
    const error = params.get('error');

    if (xeroConnected === 'true') {
      setAlertDialog({ isOpen: true, message: 'Xero connected successfully!', type: 'success' });
      // Clear URL params
      window.history.replaceState({}, document.title, window.location.pathname);
      // Refresh connection status
      checkXeroConnection();
    } else if (error) {
      setAlertDialog({ isOpen: true, message: `Xero connection error: ${decodeURIComponent(error)}`, type: 'error' });
      // Clear URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    // Mount only — this reads the OAuth result out of the URL, which happens
    // exactly once per redirect back from Xero. See the note above on why
    // checkXeroConnection is not listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Mirror the read-only state onto <body>.
   *
   * .content-inner carries the same attribute for in-page controls, but modals
   * render through a portal attached to document.body and are therefore outside
   * it — a save button in a modal would stay live. Setting it on body means the
   * one CSS rule reaches both. The Blend Calculator carve-out is applied here
   * too, so its modals keep working.
   */
  useEffect(() => {
    const shouldMark = isReadOnly(user) && activeMenu !== 'blend-calculator';
    if (shouldMark) document.body.setAttribute('data-readonly', 'true');
    else document.body.removeAttribute('data-readonly');
    return () => document.body.removeAttribute('data-readonly');
  }, [user, activeMenu]);

  // Publishes --kb-inset for anything that must sit clear of a phone keyboard.
  // Mounted once here rather than per dropdown: it is one listener and a CSS
  // variable, and every menu in the app reads it from the stylesheet.
  useKeyboardInset();

  /*
   * The phone shell's CSS hook. Every rule in RepMobile.css is scoped to this
   * attribute rather than to a media query, because the trigger is "REP badge
   * AND narrow" and a media query cannot express the first half — it would fire
   * for an office user on a phone too.
   */
  useEffect(() => {
    if (repMobile) document.body.setAttribute('data-rep-mobile', 'true');
    else document.body.removeAttribute('data-rep-mobile');
    return () => document.body.removeAttribute('data-rep-mobile');
  }, [repMobile]);

  /*
   * The page's width on a phone (utils/viewportFit). The rep shell keeps the
   * phone's own width; anyone else on a phone-sized touch screen gets the
   * desktop laid out at 1280px and scaled to fit, so it opens whole rather
   * than zoomed in on its left edge with a sideways scroll. Decided from the
   * DEVICE's screen, never the layout — once the layout is 1280 wide every
   * width query says "desktop". Put back to the device width on sign-out.
   */
  useEffect(() => {
    applyViewport(viewportContentFor({
      signedIn: !!user,
      rep: badgeOf(user) === 'REP',
      repMobile,
      smallTouchScreen: isSmallTouchScreen()
    }));
  }, [user, repMobile]);
  useEffect(() => () => applyViewport(DEVICE_VIEWPORT), []);

  // The screen's name on <body>, which readOnlyFetch sends with every API
  // request (X-App-Source) so the audit trail says where a change was made.
  useEffect(() => {
    document.body.dataset.screen = activeMenu === 'home' ? 'Home' : getPageTitle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMenu, pageTitleOverride]);

  /*
   * The session has lapsed. Set by the 401 the fetch guard raises, and by the
   * poll below so an idle tab finds out too rather than waiting for whatever
   * the user does next to fail.
   *
   * One-way: nothing clears it, because nothing can. The only exit is signing
   * in again, which reloads the app.
   */
  useEffect(() => {
    const onExpired = () => setSessionExpired(true);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  /*
   * Whether this user's Xero grant is short of the scopes we now request, and
   * close enough to the deadline to say so. The server decides both halves —
   * see xeroService.scopeMigration — so a skewed office clock cannot start the
   * prompt early or suppress it entirely.
   */
  const [xeroScopeNag, setXeroScopeNag] = useState(null);

  const checkXeroConnection = async () => {
    // A rep neither pushes to nor pulls from Xero, so there is nothing to check
    // and nothing to prompt about. Bailing here is what keeps the status pill
    // and the connect prompt from ever appearing for them.
    if (xeroRequirement(user) === 'blocked') {
      setXeroConnected(false);
      setCheckingXero(false);
      return;
    }
    /*
     * Only a real answer changes what we show. A network blip (the office
     * link, Starlink), our own server restarting mid-deploy (502), or the
     * status route failing on its own (503) is not evidence that anyone's
     * Xero connection has gone — and treating it as such put the blocking
     * gate over the whole screen until the next poll five minutes later. So
     * on any of those the last known state stands, and if there is none yet
     * we stay "Checking…" (the gate never shows while checking) and look
     * again shortly. A 401 is the session check's business, not this one's.
     */
    const recheckSoon = () => {
      clearTimeout(xeroRecheckTimer.current);
      xeroRecheckTimer.current = setTimeout(checkXeroConnection, 15000);
    };
    let data;
    try {
      const response = await fetch(`${API_URL}/api/auth/xero/status`, {
        credentials: 'include'
      });
      if (!response.ok) { recheckSoon(); return; }
      data = await response.json();
    } catch (error) {
      recheckSoon();
      return;
    }
    const custom = data.mode === 'custom';
    setXeroMode(custom ? 'custom' : 'user');
    setXeroConnected(!!data.connected);
    setXeroScopeNag(
      !custom && data.connected && data.scopeMigration?.reconnectRequired
        ? data.scopeMigration
        : null
    );
    setCheckingXero(false);
  };

  const connectToXero = () => {
    window.location.href = `${API_URL}/api/auth/connect`;
  };

  const disconnectXero = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Disconnect from Xero',
      message: 'Are you sure you want to disconnect from Xero? You will need to reconnect to sync data.',
      type: 'warning',
      confirmText: 'Disconnect',
      onConfirm: doDisconnectXero
    });
  };

  const doDisconnectXero = async () => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
    try {
      const response = await fetch(`${API_URL}/api/auth/xero/disconnect`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        setXeroConnected(false);
        setAlertDialog({ isOpen: true, message: 'Xero disconnected successfully', type: 'success' });
      } else {
        setAlertDialog({ isOpen: true, message: 'Failed to disconnect from Xero', type: 'error' });
      }
    } catch (error) {
      console.error('Error disconnecting from Xero:', error);
      setAlertDialog({ isOpen: true, message: 'Error disconnecting from Xero', type: 'error' });
    }
  };

  const getUserInitials = () => {
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    return (firstName[0] || '') + (lastName[0] || '');
  };

  // Resolve an incoming menu id (nav click, saved favourite, #hash) through
  // LEGACY_TAB_ROUTES. Ids that are now tabs select their tab and return the
  // page that absorbed them; everything else passes straight through. Only
  // ever called from event handlers and effects, never during render.
  const resolveRoute = (menuId) => {
    const legacy = LEGACY_TAB_ROUTES[menuId];
    if (!legacy) return menuId;
    setPageTabs(prev => ({ ...prev, [legacy.menu]: legacy.tab }));
    return legacy.menu;
  };

  // Helper to find parent menu for a given submenu item
  const findParentMenu = (menuId, items) => {
    for (const item of items) {
      if (item.subItems) {
        const subItem = item.subItems.find(sub => sub.id === menuId);
        if (subItem) {
          return item.id;
        }
      }
    }
    // Pages reached from a header button rather than a submenu entry (Add
    // Invoice, Add Bill, Create Order…) aren't in `items`, so fall back to the
    // parent registered for them in navItemById — otherwise the main menu
    // deselects its section the moment you open one.
    return navItemById[menuId]?.parentId || menuId; // else it's a main menu item
  };

  // Navigate to a menu item, properly updating main menu and submenu state.
  // Pushes a history entry so the browser back / forward buttons work, unless
  // called from the popstate handler with skipHistory=true (we mustn't push
  // when reacting to back/forward, or the stack would diverge).
  const navigateToMenu = (rawMenuId, params, { skipHistory = false } = {}) => {
    // An id that used to be its own page is now a tab — land on the owning
    // page with that tab selected rather than bouncing to home.
    const menuId = resolveRoute(rawMenuId);

    // Land on a particular tab of the page — "Find in …" (utils/revealInSearch)
    // passes the tab its list lives on. Without it a page left on another tab
    // stays there, and when that page is already the active one nothing
    // changes at all, so the list never mounts to take the search handed to it.
    if (params?.tab) {
      setPageTabs(prev => ({ ...prev, [menuId]: params.tab }));
    }

    // Check for edit mode title overrides
    if (menuId === 'create-purchase-order' && sessionStorage.getItem('editPOId')) {
      setPageTitleOverride('Edit Purchase Order');
    } else if (menuId === 'create-invoice' && sessionStorage.getItem('editInvoiceId')) {
      setPageTitleOverride('Edit Invoice');
    } else if (menuId === 'create-credit-note' && sessionStorage.getItem('editInvoiceId')) {
      setPageTitleOverride('Edit Credit Note');
    } else if (menuId === 'create-customer-order' && sessionStorage.getItem('editOrderId')) {
      setPageTitleOverride('Edit Sales Order');
    } else {
      setPageTitleOverride(null);
    }

    // Find parent menu if this is a submenu item
    const parentId = findParentMenu(menuId, menuItems);

    if (menuId !== activeMenu) {
      /*
       * The phone swaps immediately; the desktop keeps its crossfade.
       *
       * The fade works by setting .content-inner to `opacity: 0;
       * pointer-events: none`, swapping activeMenu inside a 300ms timeout, and
       * clearing the flag. That makes the new screen's visibility depend on a
       * timer firing, and a phone browser throttles timers around touch,
       * scrolling and a collapsing toolbar — when it does not fire, the screen
       * renders invisible and untappable, which is exactly "no component shows
       * until I reload".
       *
       * There is nothing to gain by keeping it here either: 300ms of blank
       * panel after every tap is a cost on a phone, not a polish.
       */
      if (repMobile) {
        setActiveMainMenu(parentId);
        setActiveMenu(menuId);
        setContentFading(false);
      } else {
        setContentFading(true);
        setTimeout(() => {
          setActiveMainMenu(parentId);
          setActiveMenu(menuId);
          setContentFading(false);
        }, 300);
      }
    } else {
      setActiveMainMenu(parentId);
    }

    if (!skipHistory && window.location.hash.slice(1) !== menuId) {
      window.history.pushState(null, '', `#${menuId}`);
    }

    // A leaf was reached, so the nav has done its job. Submenu parents never
    // get here — they toggle their children and return before this.
    collapseAfterNavigation();
  };

  /**
   * The nav, as permission-annotated configuration.
   *
   * Every entry carries the permission that reveals it; an entry with no `perm`
   * is open to anyone signed in (Home, and the landing tab of a page whose
   * parent is already gated). A section disappears when every one of its
   * children has been filtered out, so a rep does not see an empty "Purchases"
   * heading that opens onto nothing.
   *
   * The keys match config/permissionRegistry.js exactly — nav.<menu-id> — which
   * is what lets a new screen be gated by adding one row to the registry rather
   * than by editing this file and the server and the admin UI.
   */
  const MENU_CONFIG = [
    { id: 'home', label: 'Home', icon: 'home' },
    {
      id: 'contacts', label: 'Contacts', icon: 'group',
      subItems: [
        { id: 'search-contacts', label: 'Contacts', icon: 'search', perm: 'nav.search-contacts',
          create: { to: 'new-contact', perm: 'contacts.edit', label: 'Add contact' } },
        { id: 'credit-limits', label: 'Credit Control', icon: 'network_check', perm: 'credit_control' },
        { id: 'complaints', label: 'Complaints', icon: 'report_problem', perm: 'nav.complaints' }
      ]
    },
    {
      id: 'customer-orders', label: 'Sales', icon: 'sell',
      subItems: [
        { id: 'search-customer-orders', label: 'Sales Orders', icon: 'search', perm: 'nav.search-customer-orders',
          create: { to: 'create-customer-order', perm: 'orders.create', label: 'Add sales order' } },
        { id: 'standing-orders', label: 'Standing Orders', icon: 'event_repeat', perm: 'nav.standing-orders' },
        // The reps' own pricing (P7, 28 Oct 2026): the lines sent to them to price.
        // Reps only (repOnly — Cam, 30 Oct 2026: standard users and admins don't need either).
        { id: 'rep-pricing', label: 'My Pricing', icon: 'price_check', perm: 'nav.rep-pricing', repOnly: true },
        // A rep's own customers' contracts; the office's view of any rep's (O4).
        { id: 'rep-contracts', label: 'My Contracts', icon: 'handshake', perm: 'nav.rep-contracts', repOnly: true },
        { id: 'haulage-jobs', label: 'Haulage Jobs', icon: 'local_shipping', perm: 'nav.haulage-jobs',
          create: { to: 'create-haulage-job', perm: 'haulage_jobs.edit', label: 'Add haulage job' } },
        { id: 'search-till-sales', label: 'Till Sales', icon: 'point_of_sale', perm: 'nav.search-till-sales' },
        { id: 'prescriptions', label: 'Prescriptions', icon: 'prescriptions', perm: 'nav.prescriptions' },
        { id: 'search-invoices', label: 'Invoices', icon: 'receipt', perm: 'nav.search-invoices',
          create: { to: 'create-invoice', perm: 'invoices.create', label: 'Add invoice' },
          // Left of the +: a credit note is the other document this list holds.
          createBefore: { to: 'create-credit-note', perm: 'invoices.create', label: 'Add credit note', icon: 'remove' } }
      ]
    },
    {
      id: 'transport', label: 'Transport', icon: 'local_shipping',
      subItems: [
        { id: 'transport-planning', label: 'Transport Planning', icon: 'route', perm: 'nav.transport-planning' },
        { id: 'haulage-jobs-planning', label: 'Haulage/ Bag Jobs', icon: 'shopping_bag', perm: 'nav.haulage-jobs-planning' },
        { id: 'fleet-tracking', label: 'Fleet Tracking', icon: 'location_on', perm: 'nav.fleet-tracking' },
        { id: 'transport-settings', label: 'Settings', icon: 'settings', perm: 'transport_planning' }
      ]
    },
    {
      id: 'purchase-orders', label: 'Purchases', icon: 'shopping_cart',
      subItems: [
        { id: 'search-purchase-orders', label: 'Purchase Orders', icon: 'search', perm: 'nav.search-purchase-orders',
          create: { to: 'create-purchase-order', perm: 'purchase_orders.create', label: 'Add purchase order' } },
        { id: 'search-bills', label: 'Bills', icon: 'receipt_long', perm: 'nav.search-bills',
          create: { to: 'create-bill', perm: 'bills.create', label: 'Add bill' },
          // Left of the +: many supplier invoices at once.
          createBefore: { to: 'bulk-add-bills', perm: 'bills.create', label: 'Bulk add bills', icon: 'library_add' } },
        { id: 'repeat-bills', label: 'Repeat Billing', icon: 'event_repeat', perm: 'nav.repeat-bills' }
      ]
    },
    {
      id: 'statements', label: 'Statements', icon: 'format_list_bulleted',
      subItems: [
        { id: 'create-statement', label: 'Generate Statements', icon: 'format_list_bulleted_add', perm: 'nav.create-statement' },
        { id: 'statement-run-history', label: 'Run History', icon: 'history', perm: 'nav.statement-run-history', isAdmin: true }
      ]
    },
    {
      id: 'inventory', label: 'Inventory', icon: 'category',
      subItems: [
        { id: 'search-inventory', label: 'Manage Inventory', icon: 'search', perm: 'nav.search-inventory' },
        { id: 'contract-pricing', label: 'Contracts', icon: 'handshake', perm: 'nav.contract-pricing' },
        { id: 'milk-powder-pricing', label: 'Milk Powder', icon: 'water_drop', perm: 'nav.milk-powder-pricing' },
        { id: 'formulations', label: 'Formulations', icon: 'nutrition', perm: 'nav.formulations' },
        { id: 'blend-calculator', label: 'Blend Calculator', icon: 'calculate', perm: 'blend_calculator' }
      ]
    },
    {
      id: 'reports', label: 'Reports', icon: 'analytics',
      subItems: [
        { id: 'report-invoice-detail', label: 'Invoice Detail', icon: 'description', perm: 'nav.report-invoice-detail' },
        { id: 'report-xero', label: 'Xero Reports', icon: 'table_view', perm: 'nav.report-xero' },
        { id: 'report-contracts-review', label: 'Contracts Review', icon: 'fact_check', perm: 'nav.report-contracts-review' },
        { id: 'report-map-of-ag', label: 'Map of Ag', icon: 'water_drop', perm: 'nav.report-map-of-ag' },
        // Credit Control's own key: this is that screen's information, reorganised.
        { id: 'report-aged-debt', label: 'Aged Debt', icon: 'hourglass_bottom', perm: 'credit_control' }
      ]
    },
    { id: 'users', label: 'Users', icon: 'admin_panel_settings', perm: 'nav.users', isAdmin: true },
    { id: 'activity', label: 'Activity', icon: 'history', perm: 'nav.activity', isAdmin: true }
  ];

  const menuItems = MENU_CONFIG
    .map(item => {
      if (!Array.isArray(item.subItems)) return item;
      const subItems = item.subItems.filter(sub =>
        (!sub.repOnly || badgeOf(user) === 'REP') && (!sub.perm || hasPermission(user, sub.perm)));
      return { ...item, subItems };
    })
    .filter(item => {
      if (item.perm && !hasPermission(user, item.perm)) return false;
      // A section with subItems and none left is a heading onto nothing.
      return !Array.isArray(item.subItems) || item.subItems.length > 0;
    });

  /**
   * The permission that governs a screen.
   *
   * Screens reached from a header button rather than a submenu entry
   * (Create Invoice, Create PO, …) have no nav entry of their own, so they
   * inherit the permission of the list screen they belong to — a user who can
   * see Invoicing can open Create Invoice, and one who cannot, cannot reach it
   * by typing the hash either.
   */
  const HIDDEN_ROUTE_PERMISSIONS = {
    'create-invoice': 'nav.search-invoices',
    'create-credit-note': 'nav.search-invoices',
    'create-purchase-order': 'nav.search-purchase-orders',
    'create-bill': 'nav.search-bills',
    'bulk-add-bills': 'nav.search-bills',
    'create-customer-order': 'nav.search-customer-orders',
    'create-haulage-job': 'nav.haulage-jobs'
  };

  const permissionForMenu = (menuId) => {
    if (HIDDEN_ROUTE_PERMISSIONS[menuId]) return HIDDEN_ROUTE_PERMISSIONS[menuId];
    for (const item of MENU_CONFIG) {
      if (item.id === menuId) return item.perm || null;
      for (const sub of item.subItems || []) {
        if (sub.id === menuId) return sub.perm || null;
      }
    }
    return null;
  };

  const activeMenuLabel = (() => {
    for (const item of MENU_CONFIG) {
      if (item.id === activeMenu) return item.label;
      for (const sub of item.subItems || []) {
        if (sub.id === activeMenu) return sub.label;
      }
    }
    return null;
  })();


  // Build a flat id → menu-item lookup for the favourites nav (entries in
  // preferences.favourites_items reference menu ids; we need the label/icon
  // at render time).
  const navItemById = (() => {
    const map = {};
    for (const mi of menuItems) {
      map[mi.id] = { ...mi, parentId: null };
      if (Array.isArray(mi.subItems)) {
        for (const sub of mi.subItems) {
          if (sub.isHeader || sub.isDivider) continue;
          map[sub.id] = { ...sub, parentId: mi.id, parentLabel: mi.label };
        }
      }
    }
    // Hidden routes reached via a header button rather than a submenu entry.
    // Register them so refresh / deep-link resolves instead of bouncing home.
    map['create-customer-order'] = { id: 'create-customer-order', label: 'Create Order', icon: 'add_shopping_cart', parentId: 'customer-orders', parentLabel: 'Sales' };
    map['create-haulage-job'] = { id: 'create-haulage-job', label: 'Create Haulage Job', icon: 'add_shopping_cart', parentId: 'customer-orders', parentLabel: 'Sales' };
    map['create-invoice'] = { id: 'create-invoice', label: 'Add Invoice', icon: 'add', parentId: 'customer-orders', parentLabel: 'Sales' };
    map['create-credit-note'] = { id: 'create-credit-note', label: 'Add Credit', icon: 'credit_card_off', parentId: 'customer-orders', parentLabel: 'Sales' };
    map['create-purchase-order'] = { id: 'create-purchase-order', label: 'Add Purchase Order', icon: 'add', parentId: 'purchase-orders', parentLabel: 'Purchases' };
    map['create-bill'] = { id: 'create-bill', label: 'Add Bill', icon: 'add', parentId: 'purchase-orders', parentLabel: 'Purchases' };
    map['bulk-add-bills'] = { id: 'bulk-add-bills', label: 'Bulk Add Bills', icon: 'library_add', parentId: 'purchase-orders', parentLabel: 'Purchases' };
    // Ids that became tabs stay registered under the tab's label, so a saved
    // favourite or context-menu entry pointing at one still renders instead of
    // silently vanishing. Clicking it routes via resolveRoute.
    for (const [oldId, { menu, tab }] of Object.entries(LEGACY_TAB_ROUTES)) {
      if (map[oldId]) continue;
      const owner = map[menu];
      const tabDef = (TABBED_PAGES[menu] || []).find(t => t.key === tab);
      if (!owner || !tabDef) continue;
      map[oldId] = {
        id: oldId, label: tabDef.label, icon: tabDef.icon,
        parentId: owner.parentId, parentLabel: owner.parentLabel
      };
    }
    return map;
  })();

  // Keep a ref to navItemById so the popstate listener (registered once) can
  // always validate against the latest menu set — useful if menus ever
  // re-build mid-session.
  const navItemByIdRef = useRef(navItemById);
  useEffect(() => { navItemByIdRef.current = navItemById; });

  // On mount: if the URL has a hash, restore the corresponding menu (this is
  // how refresh / direct links / back-from-another-tab end up on the right
  // screen). If the hash is empty or unknown, normalise to #home.
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (!hash) {
      window.history.replaceState(null, '', '#home');
      return;
    }
    const legacy = LEGACY_TAB_ROUTES[hash];
    if (legacy) {
      setPageTabs(prev => ({ ...prev, [legacy.menu]: legacy.tab }));
      window.history.replaceState(null, '', `#${legacy.menu}`);
      setActiveMainMenu(navItemByIdRef.current[legacy.menu]?.parentId || legacy.menu);
      setActiveMenu(legacy.menu);
      return;
    }
    const item = navItemByIdRef.current[hash];
    if (!item) {
      window.history.replaceState(null, '', '#home');
      setActiveMenu('home');
      setActiveMainMenu('home');
      return;
    }
    setActiveMainMenu(item.parentId || hash);
    // activeMenu was already seeded from the hash via useState's initialiser.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // popstate: react to browser back/forward by re-applying the hash to state
  // without pushing a new history entry (skipHistory is implicit because we
  // call setActiveMenu directly rather than navigateToMenu).
  useEffect(() => {
    const handler = () => {
      const hash = window.location.hash.slice(1) || 'home';
      const legacy = LEGACY_TAB_ROUTES[hash];
      const target = legacy ? legacy.menu : hash;
      if (legacy) setPageTabs(prev => ({ ...prev, [legacy.menu]: legacy.tab }));
      const item = navItemByIdRef.current[target];
      if (!item) return;
      setActiveMainMenu(item.parentId || target);
      setActiveMenu(target);
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  // "View all" from the contact modal's Payment Behaviour panel: pre-load the
  // contact name into the Payments search and jump to the Credit & Payments
  // page's Payments tab. Setters from useState are stable, so [] deps are safe.
  useEffect(() => {
    const handler = (e) => {
      const name = e.detail?.contactName || '';
      useNavStore.getState().setPage('searchPayments.filters', {
        search: name, dateFrom: '', dateTo: '', status: '', is_reconciled: '', limit: 50
      });
      useNavStore.getState().setPage('searchPayments.page', 1);
      setPageTabs(prev => ({ ...prev, 'credit-limits': 'payments' }));
      setActiveMainMenu('contacts');
      setActiveMenu('credit-limits');
      if (window.location.hash.slice(1) !== 'credit-limits') {
        window.history.pushState(null, '', '#credit-limits');
      }
    };
    window.addEventListener('excel:view-payments', handler);
    return () => window.removeEventListener('excel:view-payments', handler);
  }, []);

  // Render one Favourites entry. Entry shape:
  //   { type: 'item', id }
  //   { type: 'section', label }
  //   { type: 'divider' }
  //   { type: 'submenu', id, label, icon, children: [...] }
  const renderFavouriteEntry = (entry, keyPrefix) => {
    if (!entry) return null;
    const k = `${keyPrefix}`;
    if (entry.type === 'divider') {
      return <div key={k} className="nav-sub-divider" />;
    }
    if (entry.type === 'section') {
      return <div key={k} className="nav-favourite-section">{entry.label || ''}</div>;
    }
    if (entry.type === 'submenu') {
      const submenuKey = `fav-sub-${entry.id}`;
      const isOpen = expandedFavSubmenus.has(submenuKey);
      const kids = Array.isArray(entry.children) ? entry.children : [];
      return (
        <div key={k} className={`nav-favourite-submenu ${isOpen ? 'expanded' : ''}`}>
          <div
            className="nav-item sub-item nav-favourite-submenu-header"
            onClick={() => {
              setExpandedFavSubmenus(prev => {
                const next = new Set(prev);
                if (next.has(submenuKey)) next.delete(submenuKey); else next.add(submenuKey);
                return next;
              });
            }}
          >
            {entry.icon && (
              <span className="material-symbols-rounded" style={{ fontSize: 18, width: 22 }}>{entry.icon}</span>
            )}
            <span className="nav-label">{entry.label || 'Submenu'}</span>
            <span className="material-symbols-rounded nav-chevron nav-chevron-sm">expand_more</span>
          </div>
          <div className="nav-favourite-submenu-items">
            {kids.map((child, i) => renderFavouriteEntry(child, `${k}-${i}`))}
          </div>
        </div>
      );
    }
    // type === 'item' (default)
    const src = navItemById[entry.id];
    if (!src) return null;
    return (
      <div
        key={k}
        className={`nav-item sub-item ${activeMenu === entry.id ? 'active' : ''}`}
        onClick={() => {
          handleNavigation(entry.id);
          if (src.parentId) setActiveMainMenu(src.parentId);
          collapseSections();
        }}
      >
        <span className="nav-label">{src.label}</span>
      </div>
    );
  };

  // The Favourites nav section, rendered under Home when preferences provide
  // at least one entry.
  const renderFavouritesSection = () => {
    const items = preferences.favourites_items;
    if (!items || items.length === 0) return null;
    const isExpanded = isSectionOpen('favourites');
    return (
      <div key="favourites-section" className={`nav-section favourites ${isExpanded ? 'expanded' : ''}`}>
        <div
          className="nav-section-header"
          onClick={() => toggleSection('favourites')}
        >
          <span style={{ width: '26px' }} className="material-symbols-rounded">star</span>
          <span className="nav-label">Favourites</span>
          <span className="material-symbols-rounded nav-chevron">expand_more</span>
        </div>
        <div className="nav-section-items" ref={el => { sectionRefs.current.favourites = el; }}>
          <NavTree containerRef={{ current: sectionRefs.current.favourites }} isCollapsed={sidebarCollapsed} />
          {items.map((entry, i) => renderFavouriteEntry(entry, `fav-${i}`))}
        </div>
      </div>
    );
  };

  // Get current page title
  const getPageTitle = () => {
    switch(activeMenu) {
      case 'home': return '';
      // Section headers
      case 'contacts': return 'Contacts';
      case 'customer-orders': return 'Sales';
      case 'transport': return 'Transport';
      case 'purchase-orders': return 'Purchases';
      case 'statements': return 'Statements';
      case 'inventory': return 'Inventory';
      case 'reports': return 'Reports';
      // Contacts
      case 'search-contacts': return 'Contacts';
      case 'new-contact': return 'Add Contact';
      case 'credit-limits': return 'Credit Control';
      case 'complaints': return 'Complaints';
      // Sales
      case 'search-customer-orders': return 'Sales Orders';
      case 'rep-contracts': return badgeOf(user) === 'REP' ? 'My Contracts' : 'Reps’ Contracts';
      case 'create-customer-order': return 'Create Customer Order';
      case 'haulage-jobs': return 'Haulage Jobs';
      case 'create-haulage-job': return 'Create Haulage Job';
      case 'search-till-sales': return 'Till Sales';
      case 'prescriptions': return 'Prescriptions';
      case 'search-invoices': return 'Invoices';
      case 'create-invoice': return 'Add Invoice';
      case 'create-credit-note': return 'Add Credit';
      // Transport
      case 'transport-planning': return 'Transport Planning';
      case 'haulage-jobs-planning': return 'Haulage/ Bag Jobs';
      case 'fleet-tracking': return 'Fleet Tracking';
      case 'transport-settings': return 'Settings';
      // Purchases
      case 'search-purchase-orders': return 'Purchase Orders';
      case 'create-purchase-order': return pageTitleOverride || 'Add Purchase Order';
      case 'search-bills': return 'Bills';
      case 'create-bill': return pageTitleOverride || 'Add Bill';
      case 'bulk-add-bills': return 'Bulk Add Bills';
      case 'repeat-bills': return 'Repeat Billing';
      // Statements
      case 'create-statement': return 'Generate Statements';
      case 'statement-run-history': return 'Run History';
      // Inventory
      case 'search-inventory': return 'Manage Inventory';
      case 'contract-pricing': return 'Contracts';
      case 'milk-powder-pricing': return 'Milk Powder';
      case 'formulations': return 'Formulations';
      case 'blend-calculator': return 'Blend Calculator';
      // Reports / admin
      case 'report-invoice-detail': return 'Invoice Detail';
      case 'users': return 'User Management';
      case 'activity': return 'Activity';
      /*
       * Anything not named above falls back to its own nav label.
       *
       * The cases exist for the screens whose title deliberately differs from
       * the menu — "Invoicing" for search-invoices, "Sales Orders" for
       * search-customer-orders. Everything else was repeating the label
       * verbatim, and a screen added to MENU_CONFIG without also being added
       * here got "Excel Agri Dashboard", silently: Standing Orders shipped
       * that way. A default that reads the nav it is already navigating
       * cannot go stale.
       */
      default: return navItemById[activeMenu]?.label || 'Excel Agri Dashboard';
    }
  };

  /**
   * The '+' on a nav sub-item. A new contact is a modal, so it opens over
   * Contacts — the search it belongs to is what sits behind it. The
   * others are screens of their own, and each returns to its search on save.
   */
  const openCreateFromNav = (section, subItem, create = subItem.create) => {
    const tooltip = document.getElementById('sub-item-tooltip');
    if (tooltip) tooltip.classList.remove('visible');
    setActiveMainMenu(section.id);
    collapseSections();
    if (create.to === 'new-contact') {
      if (activeMenu !== subItem.id) handleNavigation(subItem.id);
      setShowContactModal(true);
      return;
    }
    handleNavigation(create.to);
  };

  const handleNavigation = (rawMenu) => {
    // Special handling for new-contact - open modal instead of navigating
    if (rawMenu === 'new-contact') {
      setShowContactModal(true);
      return;
    }

    const newMenu = resolveRoute(rawMenu);

    if (newMenu !== activeMenu) {
      setContentFading(true);
      setTimeout(() => {
        setActiveMenu(newMenu);
        setContentFading(false);
      }, 500); // Wait for full fade-out before switching
      if (window.location.hash.slice(1) !== newMenu) {
        window.history.pushState(null, '', `#${newMenu}`);
      }
    }

    // A page was actually reached, so the nav has done its job on a compact
    // display. Section headers do not come through here — they only toggle
    // the open sections — so opening a submenu never closes the menu under the
    // click that opened it.
    collapseAfterNavigation();
  };

  return (
    <AccessProvider user={user}>
    <SearchRevealContext.Provider value={{ onNavigate: navigateToMenu, activeMenu, activeTab: activeTabFor(activeMenu) }}>
    <EntityModalProvider onNavigate={navigateToMenu}>
    {/* A column shell so the banner can push the app down rather than sit over
        it. The container itself keeps every other layout assumption it had. */}
    <div className="app-shell" data-rep-mobile={repMobile ? 'true' : undefined}>
      <MaintenanceBanner
        phase={maintPhase}
        lock={maintLock}
        secondsUntilLock={secondsUntilLock}
      />
      {/* Is Xero itself answering? Advisory, for everyone who uses Xero. */}
      <XeroHealthBanner
        health={xeroHealth}
        isAdmin={user.is_admin === true}
        hidden={xeroRequirement(user) === 'blocked'}
      />
      <XeroScopeBanner
        active={!!xeroScopeNag}
        retiresOn={xeroScopeNag?.retiresOn}
        onReconnect={connectToXero}
        onDisconnect={disconnectXero}
      />
      <div className="dashboard-container">

      {/* Phone: a condensed top bar instead of the fixed bottom status bar,
          which would collide with the tab bar and the home indicator. */}
      {repMobile && (
        <RepMobileHeader
          user={user}
          title={activeMenu === 'home' ? `${greetingFor()}, ${user.first_name}` : getPageTitle()}
          onOpenProfile={() => setShowUserProfile(true)}
        />
      )}

      {/* Sidebar */}
      {/* On a compact display the nav floats over the content instead of
          displacing it — a scrim catches the click that closes it again. */}
      {!repMobile && isCompactNav && !sidebarCollapsed && (
        <div className="sidebar-scrim" onClick={() => setSidebarCollapsed(true)} />
      )}
      {/* The 260px absolute sidebar is not made responsive — its 28px nav rows,
          its 5px collapse handle and NavTree's rect measuring are all desktop
          assumptions. The phone gets RepMobileNav instead. */}
      {!repMobile && (
      <div className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}${isCompactNav ? ' overlay' : ''}`}
           ref={sidebarRef}>
        <button
          className="sidebar-collapse-btn"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <span className="material-symbols-rounded">chevron_left</span>
        </button>
        <div className="sidebar-header">
          <img
            src={theme === 'light' ? '/excel-agri-logo-dark.svg' : '/excel-agri-logo.svg'}
            alt="Excel Agri"
            className="logo-icon"
          />
          <div className="sidebar-title">
            <span className="sidebar-brand">Excel Agri</span>
            <span className="sidebar-version">YOKE v{appVersion}</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map(item => {
            // Direct navigation item (no sub-items) — Home, Users
            if (!item.subItems) {
              const direct = (
                <div
                  key={item.id}
                  className={`nav-item direct ${activeMenu === item.id ? 'active' : ''} ${item.isAdmin ? 'admin-item' : ''}`}
                  onClick={() => {
                    if (activeMenu !== item.id) {
                      setContentFading(true);
                      setTimeout(() => {
                        setActiveMainMenu(item.id);
                        setActiveMenu(item.id);
                        setContentFading(false);
                      }, 300);
                      if (window.location.hash.slice(1) !== item.id) {
                        window.history.pushState(null, '', `#${item.id}`);
                      }
                    }
                    // Outside the guard, and easy to miss: Home and Users are
                    // the two items that navigate from here rather than through
                    // handleNavigation, so they were the two that left the menu
                    // standing open. Clicking the page you are already on is
                    // still a choice made and finished with.
                    collapseAfterNavigation();
                  }}
                >
                  {item.isSvg ? (
                    <CustomIcon name={item.icon} className="nav-svg-icon" />
                  ) : (
                    <span className="material-symbols-rounded">{item.icon}</span>
                  )}
                  <span className="nav-label">{item.label}</span>
                </div>
              );
              // Inject the Favourites section directly under Home.
              if (item.id === 'home' && preferences.favourites_items && preferences.favourites_items.length > 0) {
                return (
                  <React.Fragment key="home-and-favourites">
                    {direct}
                    {renderFavouritesSection()}
                  </React.Fragment>
                );
              }
              return direct;
            }

            // Section with sub-items — accordion
            const isExpanded = isSectionOpen(item.id);
            const navSubItems = item.subItems.filter(s => !s.isHeader && !s.isDivider);
            return (
              <div key={item.id} className={`nav-section ${isExpanded ? 'expanded' : ''}`}>
                <div
                  className={`nav-section-header ${item.isAdmin ? 'admin-item' : ''}`}
                  onClick={() => {
                    toggleSection(item.id);
                  }}
                >
                  {item.isSvg ? (
                    <span style={{ width: '26px' }}>
                      <CustomIcon name={item.icon} className="nav-svg-icon" />
                    </span>
                  ) : (
                    <span style={{ width: '26px' }} className="material-symbols-rounded">{item.icon}</span>
                  )}
                  <span className="nav-label">{item.label}</span>
                  <span className="material-symbols-rounded nav-chevron">expand_more</span>
                </div>

                <div className="nav-section-items" ref={el => { sectionRefs.current[item.id] = el; }}>
                  <NavTree containerRef={{ current: sectionRefs.current[item.id] }} isCollapsed={sidebarCollapsed} />
                  {item.subItems.map(subItem => subItem.isHeader ? (
                    <div key={subItem.id} className="nav-divider">{subItem.label}</div>
                  ) : subItem.isDivider ? (
                    <div key={subItem.id} className="nav-sub-divider">{subItem.label || ''}</div>
                  ) : (
                    <div
                      key={subItem.id}
                      className={`nav-item sub-item ${activeMenu === subItem.id ? 'active' : ''} ${subItem.isAdmin ? 'admin-item' : ''} ${subItem.id === navSubItems[navSubItems.length - 1]?.id ? 'last-branch' : ''}`}
                      onClick={() => {
                        handleNavigation(subItem.id);
                        setActiveMainMenu(item.id);
                        collapseSections();
                      }}
                      onMouseEnter={(e) => {
                        const el = e.currentTarget;
                        /* Guided Mode, or SHIFT held, means no wait — the point
                           of both is to be told what things are without having
                           to ask. Read at hover time rather than captured in
                           state: SHIFT can go down after the pointer has
                           already arrived. */
                        const timer = setTimeout(() => {
                          const rect = el.getBoundingClientRect();
                          let tooltip = document.getElementById('sub-item-tooltip');
                          if (!tooltip) {
                            tooltip = document.createElement('div');
                            tooltip.id = 'sub-item-tooltip';
                            tooltip.className = 'sub-item-tooltip';
                            document.body.appendChild(tooltip);
                          }
                          tooltip.textContent = subItem.label;
                          tooltip.style.left = (rect.right + 8) + 'px';
                          tooltip.style.top = (rect.top + rect.height / 2) + 'px';
                          tooltip.style.transform = 'translateY(-50%)';
                          tooltip.classList.add('visible');
                        }, guidedNow() ? 0 : 1000);
                        el._tooltipTimer = timer;
                      }}
                      onMouseLeave={(e) => {
                        clearTimeout(e.currentTarget._tooltipTimer);
                        const tooltip = document.getElementById('sub-item-tooltip');
                        if (tooltip) tooltip.classList.remove('visible');
                      }}
                    >
                      <span className={`nav-label ${subItem.id}`}>{subItem.label}</span>
                      {subItem.isAdmin && <span className="admin-badge">Admin</span>}
                      {/* Straight to the create screen. Every create screen
                          already returns to this search on save, so the list is
                          what you land back on. Not shown to someone who could
                          not use it — nav items are filtered the same way — nor
                          to a read-only badge, whose create screens are inert. */}
                      {/* `createBefore` is a second shortcut left of the +, e.g.
                          Add credit note (−) beside Add invoice (+). */}
                      {[subItem.createBefore, subItem.create]
                        .filter(c => c && hasPermission(user, c.perm) && !isReadOnly(user))
                        .map(c => (
                        <span
                          key={c.to}
                          className="material-symbols-rounded nav-create"
                          role="button"
                          tabIndex={0}
                          title={c.label}
                          aria-label={c.label}
                          onClick={(e) => {
                            // The row's own click would navigate to the search
                            // and then this would navigate again.
                            e.stopPropagation();
                            openCreateFromNav(item, subItem, c);
                          }}
                          onKeyDown={(e) => {
                            if (e.key !== 'Enter' && e.key !== ' ') return;
                            e.preventDefault();
                            e.stopPropagation();
                            openCreateFromNav(item, subItem, c);
                          }}
                        >{c.icon || 'add'}</span>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="nav-divider">
        </div>
        <div className="sidebar-footer">
          <div
            className="user-section"
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            <div className="user-avatar">
              <span>{getUserInitials()}</span>
            </div>
            <span className="user-name">{user.first_name} {user.last_name}</span>
          </div>
          {showUserMenu && (
            <div
              className="user-menu"
              onMouseLeave={() => setShowUserMenu(false)}
            >
              <div className="user-info">
                <strong>{user.first_name} {user.last_name}</strong>
                <small>{user.email}</small>
              </div>
              <button className="user-menu-item" onClick={() => { setShowUserMenu(false); setShowUserProfile(true); }}>
                <span className="material-symbols-rounded">edit</span> Edit Profile
              </button>
              <button className="user-menu-item" onClick={onLogout}>
                <span className="material-symbols-rounded">logout</span> Logout
              </button>
            </div>
          )}
        </div>
      </div>

      )}

      {/* Main Container */}
      <div className="main-container">
        <div className="content-area">
          <div className="content-wrapper" style={{ '--page-title': activeMenu !== 'home' ? `'${getPageTitle()}'` : 'none' }}>
            <div className="content-panel">
              <div className="dashboard-watermark">EA</div>
              {visibleTabsFor(activeMenu).length > 1 && (
                <div className="content-tab-strip">
                  {visibleTabsFor(activeMenu).map(t => (
                    <button
                      key={t.key}
                      className={`content-tab${activeTabFor(activeMenu) === t.key ? ' active' : ''}${t.jumpTo ? ' content-tab-jump' : ''}`}
                      onClick={() => (t.jumpTo ? navigateToMenu(t.jumpTo) : setPageTab(activeMenu, t.key))}
                      title={t.jumpTo ? `Go to ${t.label}` : undefined}
                    >
                      <span className="material-symbols-rounded">{t.icon}</span>
                      {t.label}
                    </button>
                  ))}
                </div>
              )}
              {/* `data-readonly` is the CSS hook that neutralises write controls
                  for a view-only account. Screens mark those controls with
                  data-write-action; the rule lives in App.css. The Blend
                  Calculator is excluded because it is the one place a rep does
                  real work — the same carve-out the server makes in
                  middleware/readOnly.js. */}
              <div
                ref={contentInnerRef}
                data-readonly={isReadOnly(user) && activeMenu !== 'blend-calculator' ? 'true' : undefined}
                className={`content-inner ${contentFading ? 'fading' : ''} ${activeMenu === 'home' ? 'home-active' : ''} ${visibleTabsFor(activeMenu).length > 1 ? 'tabbed' : ''}`}
              >
              {/* Defence in depth for direct #hash navigation, browser back, and a
                  permission revoked while the tab was open. The menu already
                  hides what a user cannot reach; this catches everything that
                  arrives without going through the menu. */}
              {(() => {
                const screenPerm = permissionForMenu(activeMenu);
                if (screenPerm && !hasPermission(user, screenPerm)) {
                  return <AccessDenied permission={activeMenuLabel || undefined} />;
                }
                return (<>
              {activeMenu === 'home' && (
                <DashboardHome
                  user={user}
                  variant={repMobile ? 'phone' : 'desktop'}
                  xeroConnected={xeroConnected}
                  checkingXero={checkingXero}
                  onReconnect={xeroMode === 'custom' ? null : connectToXero}
                  onDisconnect={xeroMode === 'custom' ? null : disconnectXero}
                  dashboardSettings={dashboardSettings}
                  savingDashboard={savingDashboard}
                  saveDashboardSettings={saveDashboardSettings}
                  saveWidgetSettings={saveWidgetSettings}
                  resetDashboardSettings={resetDashboardSettings}
                />
              )}

              {activeMenu === 'search-contacts' && (() => {
                const tab = activeTabFor('search-contacts');
                if (tab === 'groups') return <ContactGroups />;
                if (tab === 'reps') return <RepsManager />;
                return <SearchContacts user={user} />;
              })()}

              {/* The Blend Calculator is desktop-only for a rep. RepMobileNav
                  already declines to offer it, but #blend-calculator is
                  typeable and bookmarkable, so the route says so too. */}
              {repMobile && REP_DESKTOP_ONLY[activeMenu] && (
                <div className="rep-desktop-only-screen">
                  <span className="material-symbols-rounded">desktop_windows</span>
                  <h3>Open this on a laptop</h3>
                  <p>{REP_DESKTOP_ONLY[activeMenu]}</p>
                </div>
              )}

              {activeMenu === 'standing-orders' && (
                <StandingOrders user={user} onNavigate={(menu, params) => navigateToMenu(menu, params)} />
              )}

              {activeMenu === 'rep-pricing' && (
                <RepPricing user={user} />
              )}
              {activeMenu === 'rep-contracts' && <RepContracts />}

              {activeMenu === 'search-invoices' && (
                <SearchInvoices user={user} onNavigate={(menu, params) => navigateToMenu(menu, params)} />
              )}

              {activeMenu === 'create-invoice' && (
                <CreateInvoice key={`ci-${editRemount}`} onNavigate={(menu, params) => navigateToMenu(menu, params)} />
              )}

              {activeMenu === 'create-credit-note' && (
                <CreateInvoice key={`cn-${editRemount}`} onNavigate={(menu, params) => navigateToMenu(menu, params)} isCredit={true} />
              )}

              {activeMenu === 'create-statement' && (
                <GenerateStatements user={user} />
              )}

              {activeMenu === 'statement-run-history' && user.is_admin && (
                <StatementRunHistory />
              )}

              {activeMenu === 'search-purchase-orders' && (() => {
                const tab = activeTabFor('search-purchase-orders');
                if (tab === 'pricing-costing') return <PricingCosting from="purchases" user={user} onNavigate={(menu, params) => navigateToMenu(menu, params)} />;
                return (
                  <SearchPurchaseOrders
                    user={user}
                    onNavigate={(menu, params) => navigateToMenu(menu, params)}
                  />
                );
              })()}

              {activeMenu === 'create-purchase-order' && (
                <CreatePurchaseOrder
                  key={`cpo-${editRemount}`}
                  user={user}
                  onNavigate={(menu, params) => navigateToMenu(menu, params)}
                />
              )}

              {activeMenu === 'search-bills' && (
                <SearchBills
                  user={user}
                  onNavigate={(menu, params) => navigateToMenu(menu, params)}
                />
              )}

              {activeMenu === 'create-bill' && (
                <CreateBill
                  key={`cb-${editRemount}`}
                  onNavigate={(menu, params) => navigateToMenu(menu, params)}
                  onTitleChange={setPageTitleOverride}
                />
              )}

              {activeMenu === 'bulk-add-bills' && (
                <BulkAddBills
                  onNavigate={(menu, params) => navigateToMenu(menu, params)}
                />
              )}

              {activeMenu === 'repeat-bills' && (
                <SearchRepeatingBills user={user} />
              )}

              {activeMenu === 'search-till-sales' && (() => {
                const tab = activeTabFor('search-till-sales');
                if (tab === 'contacts') return <SyncContacts />;
                if (tab === 'inventory') return <SyncProducts />;
                return <TillSales user={user} onNavigate={(menu, params) => navigateToMenu(menu, params)} />;
              })()}

              {activeMenu === 'create-customer-order' && (
                <CreateCustomerOrder
                  key={`cco-${editRemount}`}
                  user={user}
                  onNavigate={(menu, params) => navigateToMenu(menu, params)}
                />
              )}

              {activeMenu === 'search-customer-orders' && (() => {
                const tab = activeTabFor('search-customer-orders');
                if (tab === 'pricing-costing') return <PricingCosting from="sales" user={user} onNavigate={(menu, params) => navigateToMenu(menu, params)} />;
                return (
                  <SearchCustomerOrders
                    user={user}
                    onNavigate={(menu, params) => navigateToMenu(menu, params)}
                  />
                );
              })()}

              {activeMenu === 'haulage-jobs' && (
                <SearchHaulageJobs
                  user={user}
                  onNavigate={(menu, params) => navigateToMenu(menu, params)}
                />
              )}

              {activeMenu === 'create-haulage-job' && (
                <CreateHaulageJob
                  user={user}
                  onNavigate={(menu, params) => navigateToMenu(menu, params)}
                />
              )}

              {activeMenu === 'prescriptions' && (
                <Prescriptions
                  user={user}
                  onNavigate={(menu, params) => navigateToMenu(menu, params)}
                />
              )}

              {activeMenu === 'credit-limits' && (() => {
                const tab = activeTabFor('credit-limits');
                if (tab === 'payments') return <SearchPayments user={user} onNavigate={(menu, params) => navigateToMenu(menu, params)} />;
                // Sits inside Credit Control so it inherits the credit_control
                // permission gate on the menu item — only credit-control users
                // see unallocated credits at all.
                if (tab === 'credits') return <CreditAllocations user={user} />;
                return <CreditLimitManagement user={user} />;
              })()}

              {activeMenu === 'complaints' && (
                <Complaints
                  user={user}
                  onNavigate={(menu, params) => navigateToMenu(menu, params)}
                />
              )}

              {activeMenu === 'search-inventory' && (() => {
                const tab = activeTabFor('search-inventory');
                if (tab === 'additives') return <Additives />;
                if (tab === 'minerals') return <MineralPricing />;
                if (tab === 'pricing') return <InventoryPriceChanges />;
                if (tab === 'product-groups') return <ProductGroups />;
                return <SearchInventory user={user} onNavigate={(menu, params) => navigateToMenu(menu, params)} />;
              })()}

              {activeMenu === 'contract-pricing' && (() => {
                const tab = activeTabFor('contract-pricing');
                if (tab === 'blends') return <BlendPricing user={user} />;
                if (tab === 'straights') return <StraightsPricing user={user} />;
                if (tab === 'spot') return <SpotPricing />;
                if (tab === 'minerals') return <GenericContractPricing user={user} categoryId={4} keyPrefix="mineralsContractPricing" title="minerals" infoText="Manage per-customer mineral contract pricing" />;
                if (tab === 'bags') return <GenericContractPricing user={user} categoryId={7} keyPrefix="bagsContractPricing" title="bags" />;
                if (tab === 'other') return <GenericContractPricing user={user} categoryId={8} keyPrefix="otherContractPricing" title="other" infoText="Manage contract pricing for products not covered by another pricing tab" />;
                return <CompoundPricing user={user} />;
              })()}

              {activeMenu === 'milk-powder-pricing' && (
                <MilkPowderPricing activeTab={activeTabFor('milk-powder-pricing')} />
              )}

              {!repMobile && activeMenu === 'formulations' && (
                <Formulations user={user} />
              )}

              {!repMobile && activeMenu === 'blend-calculator' && hasPermission(user, 'blend_calculator') && (() => {
                const tab = activeTabFor('blend-calculator');
                if (tab === 'materials') return <BlendMaterials user={user} />;
                if (tab === 'specs') return <BlendSpecs user={user} />;
                if (tab === 'blends') {
                  // Opening a saved blend hands it to the Calculator and
                  // switches tabs — there is one place a blend is built, and it
                  // already has the grid, the live analysis and the export.
                  return (
                    <BlendSavedList
                      user={user}
                      onLoadBlend={(blend) => {
                        setBlendToLoad(blend);
                        setPageTab('blend-calculator', 'calculator');
                      }}
                    />
                  );
                }
                return (
                  <BlendCalculator
                    user={user}
                    loadBlend={blendToLoad}
                    onLoaded={() => setBlendToLoad(null)}
                  />
                );
              })()}

              {activeMenu === 'report-invoice-detail' && (
                <InvoiceDetailReport user={user} />
              )}

              {activeMenu === 'fleet-tracking' && (
                <FleetTracking />
              )}

              {activeMenu === 'transport-planning' && !repMobile && (
                <TransportPlanning user={user} onNavigate={(menu, params) => navigateToMenu(menu, params)} />
              )}

              {activeMenu === 'haulage-jobs-planning' && (
                <HaulageJobsPlanning user={user} onNavigate={(menu, params) => navigateToMenu(menu, params)} />
              )}

              {activeMenu === 'transport-settings' && (() => {
                const tab = activeTabFor('transport-settings');
                if (tab === 'drivers') return <DriversManager user={user} />;
                if (tab === 'mill-emails') return <MillEmailsManager />;
                if (tab === 'unlock-password') return <UnlockPasswordSettings />;
                if (tab === 'loads-drops') return <DayPlanSettings />;
                if (tab === 'vehicles') return <VehiclesManager user={user} />;
                return <HauliersManager user={user} />;
              })()}

              {activeMenu === 'report-aged-debt' && (
                <AgedDebtReport user={user} />
              )}

              {activeMenu === 'report-xero' && (
                <XeroReports user={user} activeTab={activeTabFor('report-xero')} />
              )}

              {activeMenu === 'report-contracts-review' && (
                <ContractsReview user={user} onNavigate={(menu, params) => navigateToMenu(menu, params)} />
              )}

              {activeMenu === 'report-map-of-ag' && (
                <MapOfAgReport user={user} activeTab={activeTabFor('report-map-of-ag')} />
              )}

              {activeMenu === 'users' && (
                <UsersDashboard activeTab={activeTabFor('users')} />
              )}

              {activeMenu === 'activity' && user.is_admin && (
                <ActivityDashboard />
              )}
                </>);
              })()}

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Phone navigation. Every tap routes through navigateToMenu, so hash
          routing, the permission gate and per-page tabs behave identically to
          the desktop nav — this only changes what the controls look like. */}
      {repMobile && (
        <RepMobileNav
          activeMenu={activeMenu}
          navItemById={navItemById}
          onNavigate={(id) => navigateToMenu(id)}
          onOpenProfile={() => setShowUserProfile(true)}
          onLogout={onLogout}
          theme={theme}
          onToggleTheme={() => {
            const root = document.documentElement;
            const next = (root.getAttribute('data-theme') || 'dark') === 'dark' ? 'light' : 'dark';
            root.setAttribute('data-theme', next);
            localStorage.setItem('theme', next);
            setTheme(next);
          }}
          appVersion={appVersion}
        />
      )}

      {/* Status Bar */}
      {!repMobile && (
      <div className="status-bar" onMouseEnter={cancelLockCollapse} onMouseLeave={scheduleLockCollapse}>
        <div className="status-bar-left">
          <button
            onClick={() => {
              const root = document.documentElement;
              const current = root.getAttribute('data-theme') || 'dark';
              const next = current === 'dark' ? 'light' : 'dark';
              root.setAttribute('data-theme', next);
              localStorage.setItem('theme', next);
              setTheme(next); // Add state to re-render icon
            }}
            className="btn-ghost"
            title="Toggle theme"
          >
            <span className="material-symbols-rounded">
              {theme === 'light' ? 'dark_mode' : 'light_mode'}
            </span>
          </button>
          <span className="status-item">
            <span className="material-symbols-rounded">person</span>
            {user.email}
          </span>
        </div>
        <div className="status-bar-right">
          {/* Ask the Data. Sits in the status bar rather than the nav because it
              is not a screen — it is a question you ask from wherever you are,
              and navigating away to ask one defeats that. No keyboard shortcut:
              Cmd-K is bound in the capture phase by BlendCalculator, so a global
              binding would work everywhere except the screen most likely to have
              somebody wondering what a customer pays. */}
          {dataQueryReady && (
            <div
              className="status-item data-query-ask clickable"
              onClick={() => setShowDataQuery(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowDataQuery(true);
                }
              }}
              title="Ask a question about your data in plain English"
            >
              <span className="material-symbols-rounded">auto_awesome</span>
              Ask the Data
            </div>
          )}
          <StatusBarAlerts user={user} />
          {user.is_admin && issueCount > 0 && (
            <div
              className="status-item status-alert issues-logged clickable"
              onClick={() => setShowIssuesModal(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowIssuesModal(true);
                }
              }}
              title="Open the system log alerting panel"
            >
              <span className="material-symbols-rounded">notification_important</span>
              Issues Logged
              <span className="status-alert-count">{issueCount}</span>
            </div>
          )}
          {isReadOnly(user) && (
            <div className="status-item readonly-status" title="Your account can view records but not change them. The Blend Calculator is the exception.">
              <span className="material-symbols-rounded">visibility</span>
              View only
            </div>
          )}
          {xeroRequirement(user) !== 'blocked' && (
          <div
            className={`status-item xero-status ${xeroConnected ? 'connected' : 'disconnected'} ${!xeroConnected && !checkingXero && xeroMode !== 'custom' ? 'clickable' : ''}`}
            onClick={!xeroConnected && !checkingXero && xeroMode !== 'custom' ? connectToXero : undefined}
            role={!xeroConnected && !checkingXero && xeroMode !== 'custom' ? 'button' : undefined}
            title={xeroMode === 'custom'
              ? 'Your Xero work goes through the system\'s own connection — there is nothing for you to connect'
              : (!xeroConnected && !checkingXero ? 'Reconnect to Xero' : undefined)}
          >
            {checkingXero ? (
              <>
                <span className="material-symbols-rounded">progress_activity</span>
                Checking...
              </>
            ) : xeroConnected ? (
              <>
                <span className="material-symbols-rounded">check_circle</span>
                Xero Connected
              </>
            ) : (
              <>
                <span className="material-symbols-rounded">cancel</span>
                Xero Disconnected
              </>
            )}
          </div>
          )}
          {/* Xero's accounting lock as this system currently sees it (refreshed
              02:50 nightly, re-read by a save it would block, and refreshable
              here). Xero should be locked to the end of last month (Cam,
              15 Oct 2026; utils/xeroLockMonth): green closed lock when it is
              (or later), red open lock when it is behind or not set. Not for
              view-only users, who post nothing. */}
          {!isReadOnly(user) && xeroLock.loaded && (
            <div className={`status-item xero-lock-wrap ${lockState.ok ? 'is-locked' : 'is-unlocked'}`}>
              <div
                className="xero-lock-status clickable"
                onClick={() => setShowLockDate(v => !v)}
                role="button"
                tabIndex={0}
                aria-expanded={showLockDate}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setShowLockDate(v => !v);
                  }
                }}
                title={`${lockState.text}.`
                  + (lockState.ok ? '' : '\nSaving an invoice or bill dated before this month asks first.')
                  + `\nPeriod lock: ${xeroLock.periodLockDate ? formatDate(xeroLock.periodLockDate) : 'none'}`
                  + ` · Year-end lock (all users): ${xeroLock.endOfYearLockDate ? formatDate(xeroLock.endOfYearLockDate) : 'none'}`
                  + (xeroLock.fetchedAt ? `\nRead from Xero: ${formatDate(xeroLock.fetchedAt)}` : '')}
              >
                <span className="material-symbols-rounded">{lockState.ok ? 'lock' : 'lock_open'}</span>
              </div>
              {/* Always rendered so it can slide: the grid column animates
                  0fr → 1fr, widening the bar and pushing its other items over. */}
              <div className={`xero-lock-extra${showLockDate ? ' open' : ''}`} aria-hidden={!showLockDate}>
                <div className="xero-lock-extra-inner">
                  <span className="xero-lock-date">
                    {xeroLock.effectiveLockDate ? formatDate(xeroLock.effectiveLockDate) : 'None'}
                  </span>
                  {lockRefreshing
                    ? <span className="xero-lock-refresh"><LoadingSpinner size="sm" /></span>
                    : (
                      <span
                        className="xero-lock-refresh clickable"
                        role="button"
                        tabIndex={showLockDate ? 0 : -1}
                        title="Read the lock date from Xero now"
                        onClick={refreshXeroLock}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); refreshXeroLock(); } }}
                      >
                        <span className="material-symbols-rounded">refresh</span>
                      </span>
                    )}
                </div>
              </div>
            </div>
          )}
          <span className="status-item app-name">YOKE</span>
          <span className="status-item">v{appVersion}</span>
        </div>
        <div className="yoke-brand">
          <img
              src={theme === 'light' ? '/Yoke-dark.svg' : '/Yoke-light.svg'}
              alt=""
              className="yoke-logo"
            />
        </div>
      </div>
      )}

      {/* Status-bar "Issues Logged" — the same alerting panel the logger
          widget shows, so an admin can act on it without navigating. */}
      <Modal
        isOpen={showIssuesModal}
        onClose={() => setShowIssuesModal(false)}
        title="Issues Logged"
        size="xl"
        className="issues-logged-modal"
      >
        <SystemLogAlerts variant="modal" onCountChange={setIssueCount} />
      </Modal>

      {/* Ask the Data. Mounted here beside the other Dashboard-level overlays,
          inside AccessProvider, so the modal can read useAccess() if it ever
          needs to. Rendered only while open so each question starts clean —
          re-opening onto a previous customer's figures would be its own kind of
          wrong answer. */}
      {showDataQuery && <DataQueryModal onClose={() => setShowDataQuery(false)} />}

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => {
          setAlertDialog({ ...alertDialog, isOpen: false });
          if (lockCollapseOnDialogClose.current) {
            lockCollapseOnDialogClose.current = false;
            setShowLockDate(false);
          }
        }}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type || 'info'}
        confirmText={confirmDialog.confirmText || 'Confirm'}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />

      {showContactModal && ReactDOM.createPortal(
        <ContactEditModal
          contact={null}
          onClose={() => setShowContactModal(false)}
          onSave={(createdInXero) => {
            setShowContactModal(false);
            if (createdInXero) {
              setAlertDialog({
                isOpen: true,
                message: 'Contact created successfully and synced to Xero!',
                type: 'success'
              });
            } else {
              setAlertDialog({
                isOpen: true,
                message: 'Contact created locally. It has not been synced to Xero.',
                type: 'info'
              });
            }
          }}
        />,
        document.body
      )}

      <ContextMenu
        menuItems={
          preferences.context_menu_items && preferences.context_menu_items.length > 0
            ? (() => {
                const flat = flattenMenuItems(menuItems);
                // Fall back to navItemById so saved entries whose page became a
                // tab still appear (see LEGACY_TAB_ROUTES).
                return preferences.context_menu_items
                  .map(id => flat.find(item => item.id === id) || navItemById[id])
                  .filter(Boolean);
              })()
            : menuItems
        }
        onNavigate={navigateToMenu}
      />

      {/*
        * Two reasons the app stops, and the session one outranks the other:
        * with no session the Xero flow cannot run either, so offering it would
        * send someone into a second failure. Everyone is subject to the session
        * gate; only accounts that require Xero see the Xero one, and only once
        * the check has actually run — rendering while `checkingXero` is still
        * true would flash it at everyone on every page load.
        */}
      {sessionExpired ? (
        <XeroRequiredGate reason="session" onLogout={onLogout} />
      ) : (!checkingXero && !xeroConnected && xeroMode !== 'custom' && xeroRequirement(user) === 'required' && (
        <XeroRequiredGate
          reason="xero"
          checking={checkingXero}
          onConnect={connectToXero}
          onLogout={onLogout}
        />
      ))}

      {showUserProfile && (
        <UserProfile
          user={user}
          onClose={() => setShowUserProfile(false)}
          preferences={preferences}
          onSavePreferences={savePreferences}
          saving={savingPrefs}
          menuItems={menuItems}
          dashboardSettings={dashboardSettings}
          onSaveDashboard={saveDashboardSettings}
          onResetDashboard={resetDashboardSettings}
          savingDashboard={savingDashboard}
        />
      )}
      </div>
    </div>
    </EntityModalProvider>
    </SearchRevealContext.Provider>
    </AccessProvider>
  );
}

export default Dashboard;